import React, { useEffect, useState } from 'react'
import { makeStyles, Stepper, Step, StepLabel } from '@material-ui/core'
import CloseButton from 'src/components/common/CloseButton'
import { GMC_LIST, GPA_LIST, GTL_LIST } from 'src/utils/constants/common'
import { GMC, GPA, GTL, NO, YES } from 'src/utils/constants/Ceo'
import TableView from 'src/components/ceo/EmployeeManagement/TableView'
import { Button, CircularProgress } from '@mui/material'
import { DETACH_MEMBER_FROM_POLICY } from 'src/services/individualService'
import { useMutation } from '@apollo/client'
import { steps } from 'src/utils/constants/EmployeeManagement'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'

const DateOfLeaving = ({
  closeFunction,
  selectedPersons,
  clientId,
  setSelectedPersons,
  refetch,
}) => {
  const classes = useStyles()
  const [policyCombinations, setPolicyCombinations] = useState([])
  const [activeStep, setActiveStep] = useState(0)
  const [memberDetails, setMemberDetails] = useState([])
  const [selectedPolicies, setSelectedPolicies] = useState([])
  const [inPolicies, setInPolicies] = useState({
    inGpa: false,
    inGtl: false,
    inGmc: false,
  })
  const [error, setErrorMessage] = useState('')
  const [success, setSuccess] = useState('')

  const groupDetails = (insuranceNameList, policyDetails) => {
    return policyDetails?.some((policy) =>
      insuranceNameList?.includes(policy?.insuranceType)
    )
      ? YES
      : NO
  }

  const getSelectedPolicies = () => {
    const selectedPolicySet = new Set()
    selectedPersons?.forEach((person) => {
      if (groupDetails(GMC_LIST, person?.policies) === YES) {
        selectedPolicySet.add(GMC)
      }
      if (groupDetails(GTL_LIST, person?.policies) === YES) {
        selectedPolicySet.add(GTL)
      }
      if (groupDetails(GPA_LIST, person?.policies) === YES) {
        selectedPolicySet.add(GPA)
      }
    })
    setPolicyCombinations(Array.from(selectedPolicySet))
  }

  const getCombos = (arr) => {
    if (!arr?.length) return [[]]
    const restCombos = getCombos(arr?.slice(1))
    return restCombos?.flatMap((combo) => [combo, [arr[0], ...combo]?.sort()])
  }

  const getCombinations = (policies) => {
    if (policies?.length) {
      const res = getCombos(policies)
        ?.filter((combo) => combo?.length)
        ?.map((combo) => combo?.join(' '))
      res?.sort((a, b) => a?.length - b?.length || a?.localeCompare(b))
      return res
    }
    return []
  }

  const togglePolicySelection = (policy) => {
    setSelectedPolicies((prev) => {
      const updatedPolicies = prev?.includes(policy)
        ? prev?.filter((item) => item !== policy)
        : [...prev, policy]

      const draft = [...updatedPolicies]
      const uniqueArray = []

      draft
        ?.flatMap((item) => item?.split(' '))
        ?.forEach((item) => {
          if (!uniqueArray?.includes(item)) {
            uniqueArray.push(item)
          }
        })

      setInPolicies({
        inGpa: uniqueArray?.includes('GPA'),
        inGtl: uniqueArray?.includes('GTL'),
        inGmc: uniqueArray?.includes('GMC'),
      })
      return updatedPolicies
    })
  }

  const [detachEmployees, { loading }] = useMutation(
    DETACH_MEMBER_FROM_POLICY(),
    {
      onError: (error) => {
        if (error && error?.graphQLErrors && error?.graphQLErrors?.length) {
          setErrorMessage(error?.graphQLErrors[0]?.message)
        }
      },
      onCompleted: (data) => {
        setSuccess(data?.deleteMemberData?.message)
        setSelectedPersons([])
        // closeFunction()
      },
    }
  )

  const detachEmployeesFromPolicy = async () => {
    const variables = {
      clientId: clientId,
      memberDetails: memberDetails,
      inGpa: String(inPolicies?.inGpa),
      inGtl: String(inPolicies?.inGtl),
      inGmc: String(inPolicies?.inGmc),
    }

    await detachEmployees({
      variables: variables,
    })
  }

  useEffect(() => {
    getSelectedPolicies()
    // eslint-disable-next-line
  }, [selectedPersons])

  return (
    <div className={classes.containerDiv}>
      <div className={classes.selectLocationHeading}>
        <p data-test='modal-heading'>Employees being detached</p>
      </div>
      <CloseButton
        id='closeButton'
        title='Close'
        handleClose={closeFunction}
        classname={classes.closeBtn}
      />
      {!success && (
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps?.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {activeStep === 0 && (
        <div className={classes.tableContainer}>
          <TableView
            data={selectedPersons}
            setData={setMemberDetails}
            sortedData={memberDetails}
            setSelectedPersons={setSelectedPersons}
          />
          {selectedPersons?.length !== memberDetails?.length && (
            <div className={classes.warning}>
              <WarningIcon />
              &nbsp;&nbsp;Each employee date should be modified to proceed
              further (or deselect){' '}
            </div>
          )}
        </div>
      )}

      {activeStep === 1 && (
        <>
          <div>Please select policies </div>
          <div className={classes.buttonContainer}>
            {getCombinations(policyCombinations)?.map((item) => (
              <Button
                key={item}
                variant={
                  selectedPolicies?.includes(item) ? 'contained' : 'outlined'
                }
                onClick={() => togglePolicySelection(item)}
                className={`${classes.policyButton} ${
                  selectedPolicies?.includes(item)
                    ? classes.selectedPolicyButton
                    : ''
                }`}
                disabled={
                  selectedPolicies?.length > 0 &&
                  !selectedPolicies?.includes(item)
                }
              >
                {item}
              </Button>
            ))}
          </div>
        </>
      )}
      {activeStep === 2 && !success && (
        <div className={classes.buttonContainer}>
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <span className={classes.error}>{error}</span>
          ) : (
            'Are you sure to submit?'
          )}
        </div>
      )}

      {success && (
        <div className={classes.buttonContainer}>
          <span className={classes.success}>
            <CheckCircleIcon className={classes.success} /> &nbsp;&nbsp;
            {success}
          </span>
        </div>
      )}

      <div className={classes.actionButtons}>
        {activeStep > 0 && !success && (
          <Button
            onClick={() => {
              setErrorMessage('')
              setActiveStep(activeStep - 1)
            }}
            className={classes.backButton}
          >
            Back
          </Button>
        )}

        {!success && (
          <Button
            onClick={() => {
              if (activeStep < steps?.length - 1) {
                setActiveStep(activeStep + 1)
              } else {
                detachEmployeesFromPolicy()
              }
            }}
            className={classes.nextBtn}
            disabled={
              (activeStep === 0 &&
                selectedPersons?.length !== memberDetails?.length) ||
              (activeStep === 1 && selectedPolicies?.length === 0)
            }
          >
            {activeStep === steps?.length - 1 ? 'Finish' : 'Next'}
          </Button>
        )}
        {success && (
          <Button
            onClick={() => {
              closeFunction()
              setSuccess('')
              refetch()
            }}
            className={classes.clsBtn}
          >
            Close
          </Button>
        )}
      </div>
    </div>
  )
}

export default DateOfLeaving

const useStyles = makeStyles({
  containerDiv: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  tableContainer: {
    width: '90%',
    padding: '20px',
  },
  closeBtn: {
    position: 'absolute',
    right: '10px',
    top: '10px',
  },
  selectLocationHeading: {
    color: '#000',
  },
  stepper: {
    width: '80%',
    marginBottom: '20px',
  },
  nextBtn: {
    marginLeft: 'auto !important',
    color: '#fff !important',
    borderColor: '#B92250 !important',
    backgroundColor: '#B92250 !important',
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5) !important',
      borderColor: 'rgba(185, 34, 80, 0.5) !important',
      backgroundColor: 'rgba(185, 34, 80, 0.5) !important',
    },
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    margin: '30px',
  },
  policyButton: {
    color: '#B92250 !important',
    borderColor: '#B92250 !important',
    '&:hover': {
      backgroundColor: '#007bff',
      color: '#B92250',
    },
    '&.Mui-disabled': {
      color: 'grey !important',
      borderColor: 'grey !important',
    },
  },
  selectedPolicyButton: {
    backgroundColor: '#B92250 !important',
    color: '#fff !important',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '95%',
    margin: '20px',
  },
  backButton: {
    color: '#fff !important',
    borderColor: '#B92250 !important',
    backgroundColor: '#B92250 !important',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  error: {
    color: 'red',
  },
  success: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: 'green',
  },
  clsBtn: {
    marginLeft: 'auto !important',
    marginRight: 'auto !important',
    color: '#fff !important',
    borderColor: '#B92250 !important',
    backgroundColor: '#B92250 !important',
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5) !important',
      borderColor: 'rgba(185, 34, 80, 0.5) !important',
      backgroundColor: 'rgba(185, 34, 80, 0.5) !important',
    },
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    color: 'red',
    margin: '10px 0px',
  },
})
